
import React, { useState, useEffect, useRef } from 'react'
import { useSearchParams, useNavigate, useLocation } from 'react-router-dom'
import { Stack, Typography, Box, TextField, Button as MuiButton, Link, alpha, useTheme, useMediaQuery } from '@mui/material'
import type { SxProps, Theme } from '@mui/material'
import GoogleIcon from '@mui/icons-material/Google'
import AppleIcon from '@mui/icons-material/Apple'
import { CheckIcon } from 'lucide-react'
import { Alert, fieldAutofillResetSx } from '@repo/ui'
import { ErrorCode } from '@repo/types'
import type { AuthFactorType } from '@repo/types'
import { workosAuthService } from 'services/workos-auth.service'
import { ROUTES } from 'routes'
import { API_BASE_URL } from 'utils'
import { useSnackbar } from 'contexts/SnackbarContext'
import { otpCodeWasSent } from 'constants/messages'
import { AuthLogo } from '../components/AuthLogo'
import { AuthPasswordField } from '../components/AuthPasswordField'
import { AuthPrimaryButton } from '../components/AuthPrimaryButton'
import { AuthInlineLink } from '../components/AuthInlineLink'
import { AuthBackButton } from '../components/AuthBackButton'
import { useAuthPageTransition, AUTH_TRANSITION_MS, AUTH_COLD_ENTER_MS } from '../components/useAuthPageTransition'
import { TwoFactorChallenge } from '../components/TwoFactorChallenge'
import { CodeInput } from 'components/input'
import { TwoFactorEnrollmentRequired } from '../components/TwoFactorEnrollmentRequired'
import {
  authFieldRootSx,
  authOtpBoxSx,
  authSsoButtonSx,
} from '../components/authFieldChrome'
import { AUTH_FIELD_LABEL_SX, AUTH_TITLE_SX } from '../components/authType'
import { useAmbientSurge } from 'layouts/LayoutAuthAmbient/ambientSurge'
import { authGlassCardSx } from '../components/authGlassCard'
import { authPx, authScaleSx } from '../components/authScale'

const API_URL = API_BASE_URL


/** Off-screen but announced — the standard visually-hidden recipe. */
const VISUALLY_HIDDEN = {
  position: 'absolute',
  width: 1,
  height: 1,
  padding: 0,
  margin: -1,
  overflow: 'hidden',
  clip: 'rect(0 0 0 0)',
  whiteSpace: 'nowrap',
  border: 0,
} as const

export const SignInPage = () => {
  const theme = useTheme()
  const [searchParams] = useSearchParams()
  // The card's entrance animation is the arriving half of the sign-up -> sign-in cross-fade.
  // On a COLD load (the website's Log in, a bookmark) there is nothing to cross-fade from, and
  // the 200ms fade-up showed as one more stage after the white page and the bare backdrop.
  // React Router gives the first location of a page load the key 'default'.
  const isColdEntry = useLocation().key === 'default'
  const navigate = useNavigate()
  // VERA-1095: cross-fade to sign-up instead of cutting. `leaveTo` replaces
  // `navigate` for the two SIGN-UP links only — every other navigate() call on
  // this page (password setup, post-auth redirects) is a destination with a
  // different shell, where a fade of THIS content would just delay the swap.
  const { leaveTo, exitSx } = useAuthPageTransition()
  const { open: notification } = useSnackbar()
  const [isLoading, setIsLoading] = useState(false)
  // `isLoading` is only ever set immediately before LEAVING this page — either a
  // post-auth `window.location.href` or a redirect out to an OAuth provider. On
  // the provider paths we know the destination, and naming it ("Taking you to
  // Google…") both explains the wait and pre-empts the surprise of landing on
  // someone else's domain. Defaults to the generic line for the password/OTP
  // paths, where the destination is just "in".
  const [loadingMessage, setLoadingMessage] = useState('Signing you in…')
  /*
    THIS SCREEN WAS A DEAD END. Every path that sets `isLoading` hands off by assigning
    `window.location.href`, and if that navigation never completes — API down, stalled DNS,
    an offline laptop, a provider having a bad morning — the page sits on "Taking you to
    Google…" indefinitely. No error, no way back, and the only affordance on screen (the
    pulsing wordmark) actively insists it is still working.

    So the wait is now bounded: past `REDIRECT_STALL_MS` the screen admits it and offers the
    escape it previously withheld. The threshold is deliberately generous — a healthy
    redirect is well under a second, so several seconds is already abnormal, and firing
    early would flash an alarming line at someone whose sign-in was merely slow.
  */
  /*
    "Code sent" CONFIRMS IN THE COLUMN, not in the corner.

    Resending fired the global snackbar, which lands bottom-right — the far corner of a
    page whose entire content is one narrow centred column. The confirmation for an action
    you just took at the bottom of that column appeared as far from it as the viewport
    allows, so the eye had to leave the task to find the answer to it.

    Local state rather than the notification bus because this message has no life outside
    this step: it answers one click, it is meaningless once the user starts typing the new
    code, and it must not survive a route change. The snackbar is the right tool for
    something that outlives its origin; this does not.

    Auto-clears after RESEND_CONFIRMATION_MS so it behaves like the toast it replaces, and
    re-arms on each resend (the effect's cleanup cancels the previous timer), so pressing
    "Try again" twice does not leave a message that expires early from the first press.
  */
  const [codeResent, setCodeResent] = useState(false)
  useEffect(() => {
    if (!codeResent) return
    const timer = window.setTimeout(() => setCodeResent(false), 4000)
    return () => window.clearTimeout(timer)
  }, [codeResent])

  const [slowRedirect, setSlowRedirect] = useState(false)
  useEffect(() => {
    // Cleared on the way out as well as armed on the way in: `Back to sign in` drops
    // `isLoading`, and without the reset a second attempt would inherit the stale flag and
    // show the stall line instantly.
    if (!isLoading) {
      setSlowRedirect(false)
      return
    }
    const REDIRECT_STALL_MS = 8000
    const timer = window.setTimeout(() => setSlowRedirect(true), REDIRECT_STALL_MS)
    return () => window.clearTimeout(timer)
  }, [isLoading])
  const [isSubmitting, setIsSubmitting] = useState(false)

  /*
    THE HALO ANSWERS THE BUTTON (Spencer, 2026-09-23: "do all the other
    suggestions"). Two states, both read by SignInBackground through the
    ambient-surge context that SignupSuccessPage already uses:

      in flight (isSubmitting) -> 0.25: the lobes quicken and draw in a little.
        "Working" is said by the field the eye is already on, instead of by a
        spinner. On failure isSubmitting drops and the field relaxes.
      leaving   (isLoading)    -> 1: the full swirl, tightening round the logo.
        NO ADDED DELAY — every success path assigns window.location.href in the
        same tick, so the swirl plays during the reload that was happening
        anyway; sign-in gets no slower. The Google/Apple hand-off also sets
        isLoading, and swirling as it leaves is right there too.

    Released on unmount so no later auth screen inherits a compressed field.
  */
  const { setSurge } = useAmbientSurge()
  useEffect(() => {
    setSurge(isLoading ? 1 : isSubmitting ? 0.25 : 0)
  }, [isLoading, isSubmitting, setSurge])
  useEffect(() => () => setSurge(0), [setSurge])
  const [isSendingCode, setIsSendingCode] = useState(false)
  const [isResendingCode, setIsResendingCode] = useState(false)
  const [resetEmailSent, setResetEmailSent] = useState(false)
  const [error, setError] = useState<string | null>(null)
  // 'form' = a page-level error that belongs to NO field. Without it, the
  // derivation below fell back to "whichever field is currently active", so a
  // failed Google sign-in rendered as a red PASSWORD box reading
  // "Authentication failed" — for a password the user never typed.
  const [errorField, setErrorField] = useState<'email' | 'password' | 'form' | null>(null)
  // Set when the password-login path reports no account for the entered
  // email (ErrorCode.AUTH_ACCOUNT_NOT_FOUND) — surfaces a "create one" link
  // rather than leaving the user stuck re-typing a password for an address
  // that was never registered.
  const [accountNotFound, setAccountNotFound] = useState(false)
  // VERA-1095: set when the password-login path reports
  // ErrorCode.AUTH_EMAIL_NOT_VERIFIED — the password was CORRECT, WorkOS is
  // just refusing password auth until the account's email is verified. This
  // is deliberately NOT rendered as a password-field error (it isn't one) —
  // see the dedicated message block below the Sign In button.
  const [emailNotVerified, setEmailNotVerified] = useState(false)
  const [isResendingVerification, setIsResendingVerification] = useState(false)
  const [email, setEmail] = useState('')
  const [showOTP, setShowOTP] = useState(false)
  // VRG-460: PASSWORD IS THE DEFAULT, not the emailed code.
  //
  // Not a preference — the page was defaulting to the SECONDARY credential.
  // `create-signup-company.use-case.ts` collects a password on the signup form
  // ("password collected on the signup form"), so every account HAS one, and
  // the emailed code is the fallback for the person who has forgotten it.
  // Leading with the code made the common case the extra click.
  //
  // Safe to flip because both branches were already complete: the password
  // branch renders "Forgot password?" AND the reverse toggle "Use verification
  // code instead" (`setShowPasswordLogin(false)`), so nobody is stranded. The
  // code branch's own "Sign in with password" link is simply no longer the
  // entry point; it still works after toggling back.
  //
  // Two things this makes reachable on FIRST PAINT that previously needed a
  // toggle, both already handled: the `emailNotVerified` block below
  // (VERA-1095 — WorkOS refuses password auth until the email is verified),
  // and the password-side branch of the field-error split at the
  // `emailFieldError`/`passwordFieldError` derivation, whose DEFAULT target
  // moves from the email field to the password field with this line.
  const [showPasswordLogin, setShowPasswordLogin] = useState(true)
  // VRG-490 §2 — a verified second factor withholds the session; either the
  // OTP-verify or the password-login path below can return this instead of
  // tokens. `challenge` holds the ticket the completing verify call needs;
  // `enrollmentRequired` is the separate D1 outcome (a platform_admin with
  // NO verified factor at all — nothing to challenge, must enroll first).
  const [challenge, setChallenge] = useState<{ ticket: string; factorType: AuthFactorType } | null>(null)
  const [enrollmentRequired, setEnrollmentRequired] = useState(false)
  const [password, setPassword] = useState('')
  const [otp, setOtp] = useState(['', '', '', '', '', ''])

  // VRG-460: the email -> verification-code change is a CROSS-FADE with an
  // animated height, not a branch swap. It used to unmount one subtree and
  // mount the other in the same frame, so the content cut and the column's
  // height jumped.
  //
  // COPIED FROM THE SIGN-UP STEPPER (`SignUpPage.tsx`), deliberately, because
  // "match the sign-up pages" is the spec. Worth naming what it is NOT: it is
  // not `@repo/ui`'s `useAnimatedHeight`. That hook exists, but NO onboarding
  // page uses it — its consumers are `@repo/ui`'s own `Modal`, three
  // `apps/app` components and `PlanComparisonMatrix`. The stepper's mechanism
  // is the one below, and copying the thing actually in use beats adopting a
  // hook this page family has never used.
  //
  // The load-bearing detail, which `SignUpPage` learned the hard way: the
  // active height is DERIVED (`panelHeights[stepIndex]`), never stored by an
  // effect that fires after the step changes. The opacity/transform fade
  // starts synchronously in the triggering render, so a height that waits on
  // an observer round-trip lands a frame later and reads as "the box snaps,
  // then the content fades" rather than as one movement.
  const STEP_TRANSITION_MS = 200
  // Email is step 0, the code is step 1. NOTE the panels appear in the JSX in
  // the OPPOSITE order (OTP first, email second) — that is the pre-existing
  // ternary order, kept so this change stays a wrapper rather than a 400-line
  // move. Order is inconsequential here: both panels occupy the SAME grid
  // cell, and the inactive one is `visibility: hidden`, so neither paint
  // stacking nor tab order depends on which comes first in the DOM.
  const stepIndex = showOTP ? 1 : 0
  const panelRefs = useRef<(HTMLDivElement | null)[]>([])
  const [panelHeights, setPanelHeights] = useState<(number | null)[]>([])
  const activePanelHeight = panelHeights[stepIndex] ?? null
  const prefersReducedMotion = useMediaQuery('(prefers-reduced-motion: reduce)')
  // The panels only exist on the default branch — `isLoading` and
  // `resetEmailSent` replace them wholesale. Keying the observer on that keeps
  // it attached to LIVE nodes: without it, a trip through the reset-sent view
  // would leave the observer watching two detached divs and the height would
  // freeze at whatever it measured before.
  const stepPanelsMounted = !isLoading && !resetEmailSent

  useEffect(() => {
    if (!stepPanelsMounted) return
    // The CCD preview pane never fires ResizeObserver callbacks (probed
    // directly — see `SignUpPage.tsx`, "0 callbacks, initial included"), and
    // some test environments have no ResizeObserver at all. Both degrade to
    // `activePanelHeight === null` -> `height: undefined` -> auto, i.e. the
    // pre-VRG-460 behaviour: no height animation, nothing clipped. The
    // cross-fade is a plain CSS transition and is unaffected either way.
    if (typeof ResizeObserver === 'undefined') return
    const observer = new ResizeObserver((entries) => {
      setPanelHeights((prev) => {
        const next = [...prev]
        let changed = false
        for (const entry of entries) {
          const idx = panelRefs.current.indexOf(entry.target as HTMLDivElement)
          if (idx === -1) continue
          // The panel's BORDER box, in its OWN layout px (`contentRect` would
          // drop padding/border).
          //
          // ⚠️ NOT `getBoundingClientRect()` (2026-10-09, Spencer: "when I zoom
          // out, it also hides social log in and create account"). That returns
          // VISUAL px, after every ancestor `zoom`, and the app's own text zoom
          // (hooks/useTextZoom: Cmd - sets `#app { zoom: 0.9, 0.8 ... }`) is
          // exactly such an ancestor. The number was then written back as this
          // wrapper's `height`, which the same zoom scales AGAIN, so the clip
          // box came out at panel x zoom^2 visually against content at panel x
          // zoom: at 0.8 the bottom 20% (92px of 461) was cut off, which is
          // the Google button and "Create one". Zooming IN overshot instead,
          // which is why it only ever showed up on zoom-out.
          //
          // `borderBoxSize` is reported in the element's local px, the same
          // space `height` is applied in (probed in Chrome 148 under `zoom`
          // 0.7/0.8/1.2: local 71.4/76.3/75 vs getBoundingClientRect
          // 50/61/90). `offsetHeight` is the fallback for engines without it.
          const h =
            entry.borderBoxSize?.[0]?.blockSize ?? (entry.target as HTMLElement).offsetHeight
          if (next[idx] !== h) {
            next[idx] = h
            changed = true
          }
        }
        return changed ? next : prev
      })
    })
    panelRefs.current.forEach((node) => {
      if (node) observer.observe(node)
    })
    return () => observer.disconnect()
  }, [stepPanelsMounted])

  /**
   * Resting geometry for one step panel. A panel already PASSED rests above
   * (-10px), one still AHEAD rests below (+10px) — the stepper's convention, so
   * moving forward always reads as content rising from beneath.
   *
   * `visibility` is this page's one addition to the copied treatment, and it
   * earns its place: the inactive panel here contains a PASSWORD FIELD, and a
   * focusable input inside an `aria-hidden` subtree is both a tab-order trap
   * (tab from the OTP boxes and the caret lands in an invisible password box)
   * and an ARIA violation. React 18 has no `inert` prop, so `visibility:
   * hidden` does the job — it removes the subtree from the tab order — and its
   * transition is DELAYED by the fade duration so the panel still fades out
   * fully before it goes. Delay 0 on the way in, or it would arrive invisible.
   */
  const stepPanelSx = (panelIndex: number): SxProps<Theme> => {
    const isActive = panelIndex === stepIndex
    const restOffset = panelIndex < stepIndex ? -10 : panelIndex > stepIndex ? 10 : 0
    const visibilityDelay = isActive ? 0 : STEP_TRANSITION_MS
    return {
      gridColumn: 1,
      gridRow: 1,
      width: '100%',
      // ⚠️ Sized by CONTENT, never stretched to the row — this is what stops the
      // panel height latching.
      //
      // The wrapper pins the row to the measured `activePanelHeight` and clips it.
      // Grid's default `align-items: stretch` then stretches this panel to that row,
      // so its border box IS the row height — and `getBoundingClientRect()` in the
      // observer reports the pinned number back, not the content's real height. The
      // measurement becomes self-referential: the height decides what the observer
      // sees, which decides the height. Once anything makes the content taller —
      // a field error appearing under the password input is the everyday case — the
      // observer sees no change, the wrapper never grows, and the overflow is clipped
      // PERMANENTLY rather than for the length of the transition.
      //
      // `start` takes the panel out of the stretch, so it is measured at its natural
      // height and the wrapper follows it. Visually identical while the content fits,
      // because a panel that is shorter than the row was already painting from the top.
      alignSelf: 'start',
      opacity: isActive ? 1 : 0,
      transform: `translateY(${isActive ? 0 : restOffset}px)`,
      visibility: isActive ? 'visible' : 'hidden',
      // An inactive panel must never intercept a pointer event meant for the
      // one fading in over it.
      pointerEvents: isActive ? 'auto' : 'none',
      transition: prefersReducedMotion
        ? `opacity ${STEP_TRANSITION_MS}ms ease, visibility 0s linear ${visibilityDelay}ms`
        : `opacity ${STEP_TRANSITION_MS}ms ease, transform ${STEP_TRANSITION_MS}ms ease, visibility 0s linear ${visibilityDelay}ms`,
    }
  }

  // Check for error from callback
  useEffect(() => {
    const errorParam = searchParams.get('error')
    if (errorParam) {
      const detail = decodeURIComponent(errorParam)
      // A callback error is never a field error — the user was on the identity
      // provider's domain, not this form. Naming the provider is the whole point:
      // "Authentication failed" under the password box sends someone off resetting
      // a password that was never the problem.
      const isSocial = searchParams.get('source') === 'social'
      setError(
        isSocial
          ? `Google sign-in didn't complete. ${detail} You can try again, or sign in with your email and password.`
          : detail,
      )
      setErrorField('form')
    }
  }, [searchParams])

  /**
   * Send OTP code to user's email
   */
  const handleReceiveCode = async () => {
    if (!email) return

    console.log('[SignInPage] Sending OTP code to:', email)
    setIsSendingCode(true)
    setError(null)

    try {
      const result = await workosAuthService.sendMagicAuthCode(email)

      console.log('[SignInPage] Send OTP result:', result)
      if (result.success) {
        console.log('[SignInPage] OTP sent successfully, showing OTP input')
        // `CodeInput autoFocus` handles the caret when the step mounts, so the
        // old setTimeout-into-a-ref dance is gone with the refs it depended on.
        setShowOTP(true)
      } else {
        console.error('[SignInPage] Failed to send OTP:', result.error)
        setError(result.error || 'Failed to send verification code')
      }
    } catch (err) {
      console.error('[SignInPage] Error sending code:', err)
      setError('Failed to send verification code. Please try again.')
    } finally {
      setIsSendingCode(false)
    }
  }

  /**
   * Forgot password (VERA-1095) — requests a real, WorkOS-backed password
   * reset LINK (single-use, expiring token), emailed to the user. They
   * complete the reset on /reset-password, then return here to sign in.
   *
   * Replaces the previous behaviour, which reused the passwordless OTP send
   * and claimed a "link" was emailed when it was actually a 6-digit code
   * with no box to enter it in — a dead end. See auth.service.ts's
   * requestPasswordReset docblock for the full history.
   *
   * Distinguishes "no account for this email" (ErrorCode.AUTH_ACCOUNT_NOT_FOUND)
   * the same way the password-login path does — a deliberate, accepted
   * enumeration tradeoff scoped to password flows (see handlePasswordLogin).
   */
  const handleForgotPassword = async () => {
    if (!email) {
      setError('Enter your email first, then click Forgot password.')
      setErrorField('email')
      document.querySelector<HTMLInputElement>('input[placeholder="Enter your email"]')?.focus()
      return
    }
    setIsSendingCode(true)
    setError(null)
    setAccountNotFound(false)
    try {
      const result = await workosAuthService.requestPasswordReset(email)
      if (result.success) {
        setResetEmailSent(true)
      } else if (result.errorCode === ErrorCode.AUTH_ACCOUNT_NOT_FOUND) {
        setAccountNotFound(true)
        setError("We couldn't find an account for that email.")
        setErrorField('email')
      } else {
        // VRG-550: everything that is NOT "no such account" is a condition of the REQUEST,
        // not of the email field — rate limiting, a send failure, an unreachable API. Pinning
        // those under the input reads as "this address is wrong", which is misleading and, for
        // the rate limit, actively unhelpful: the address is fine and retyping it cannot help.
        // The server's message carries the actionable detail (e.g. how many minutes remain),
        // so surface it verbatim in the form-level alert.
        setError(result.error || 'Failed to send password reset email. Please try again.')
        setErrorField('form')
      }
    } catch {
      setError('Failed to send password reset email. Please try again.')
      setErrorField('form')
    } finally {
      setIsSendingCode(false)
    }
  }

  // `submittedCode` comes from CodeInput's `onComplete`, which fires inside the
  // same event that sets the digits — so `otp` is one render behind and would
  // submit five. The button path passes nothing and reads state as before.
  const handleSubmitOtp = async (submittedCode?: string) => {
    const code = submittedCode ?? otp.join('')

    if (code.length !== 6 || isSubmitting) {
      return
    }

    setIsSubmitting(true)
    setError(null)

    try {
      const result = await workosAuthService.verifyMagicAuthCode(email, code)

      console.log('[SignInPage] Verify OTP result:', {
        success: result.success,
        hasUser: !!result.user,
        userRole: result.user?.role?.name,
        error: result.error
      })

      // VRG-490 §2 — a verified second factor withholds the session. Checked
      // BEFORE the `result.user` branch below, since neither outcome carries
      // a user.
      if (result.challengeRequired && result.ticket && result.factorType) {
        setChallenge({ ticket: result.ticket, factorType: result.factorType })
        setIsSubmitting(false)
        return
      }

      // VRG-490 D1 — a platform_admin with no verified factor at all.
      if (result.enrollmentRequired) {
        setEnrollmentRequired(true)
        setIsSubmitting(false)
        return
      }

      if (result.success && result.user) {
        console.log('[SignInPage] Authentication successful! Reloading page to initialize auth state...')

        // New users are sent to set a password. (Password RESET is now a
        // separate, link-based flow — see handleForgotPassword — so it no
        // longer routes through this OTP-submit path at all.)
        if (result.isNewUser) {
          navigate(ROUTES.PASSWORD_SETUP, { replace: true })
          return
        }

        // Show full loading screen before redirect
        setIsLoading(true)

        // Force a page reload to reinitialize the auth state
        // This ensures the useWorkOSAuth hook picks up the new token and user
        // VRG-339: land on `/` and let HomePage decide. The destination depends on this
        // user's preferences and their company's setup state, neither of which is loaded
        // yet at this point. The full reload is kept deliberately — it is what
        // reinitialises auth; only the destination changed.
        window.location.href = ROUTES.HOME
      } else {
        console.error('[SignInPage] Authentication failed:', result.error)
        setError(result.error || 'Invalid or expired verification code')
        setIsSubmitting(false)
      }
    } catch (err) {
      console.error('[SignInPage] Error verifying code:', err)
      setError('Invalid or expired verification code. Please try again.')
      setIsSubmitting(false)
    }
  }

  /**
   * Redirect to Google OAuth
   */
  const handleGoogleSignIn = () => {
    setLoadingMessage('Taking you to Google…')
    setIsLoading(true)
    setError(null)
    workosAuthService.loginWithGoogle()
  }

  const handleAppleSignIn = () => {
    setLoadingMessage('Taking you to Apple…')
    setIsLoading(true)
    setError(null)
    workosAuthService.loginWithApple()
  }

  /**
   * MICROSOFT IS GONE, DELIBERATELY AND PERMANENTLY — do not re-add this button.
   *
   * WorkOS's "Microsoft OAuth" social login is PERSONAL-ACCOUNTS-ONLY by design,
   * not by misconfiguration. Their integration guide requires the Azure app
   * registration to be set to "Personal Microsoft accounts only" (stated as
   * IMPORTANT, twice), and the authorize URL WorkOS builds confirms it —
   * measured live: `login.microsoftonline.com/consumers/oauth2/v2.0/authorize`.
   * `/consumers` accepts Microsoft accounts (outlook / hotmail / live) and
   * REJECTS every work or school Entra ID account.
   *
   * For a B2B signup that inverts the button's purpose: it fails for precisely
   * the users it exists to serve, and it fails at Microsoft's own screen where
   * we have no copy and no way to explain it. An earlier reading of this had it
   * as a fixable Azure setting; it is not, and "fixing" it that way would break
   * the connection, since WorkOS requires personal-only.
   *
   * The B2B path is a DIFFERENT product: WorkOS Entra ID OIDC, which is an
   * enterprise SSO connection configured PER ORGANIZATION in the WorkOS
   * dashboard. That cannot back a self-serve signup button — a prospect who has
   * never signed up has no organization, so there is no connection to
   * authenticate against. It belongs post-signup as an enterprise feature, not
   * here. `loginWithMicrosoft` was removed from `workos-auth.service` with the
   * same note.
   *
   * APPLE IS HIDDEN, TEMPORARILY — flip this on once Vera owns a Services ID.
   *
   * The connection currently runs on WorkOS's SHARED default credential
   * (`com.workos.default.production.signinwithapple` — measured in the same
   * probe). Two consequences: Apple's consent sheet names WorkOS rather than
   * Vera, and Apple does not permit a shared credential for production.
   *
   * The reason this is a HARD gate and not a soft one: Apple scopes both the
   * stable user identifier (`sub`) and Hide-My-Email relay addresses to the
   * DEVELOPER TEAM. Moving to Vera's own Services ID makes Apple return a
   * completely new `sub` for the same Apple ID, with `email` null — so every
   * account created on the borrowed credential stops matching its row. Apple's
   * TN3159 migration path exists but requires the LOSING team to cooperate, and
   * that team is WorkOS, on a credential they share across customers. In
   * practice there is no migration. Every Apple signup that happens before the
   * switch is one we lose, which is why hiding costs nothing now and waiting
   * costs accounts later.
   *
   * Google is unconditional: it runs on Vera's own credentials and redirects
   * cleanly to accounts.google.com.
   */
  const appleLoginEnabled = import.meta.env.VITE_ENABLE_APPLE_LOGIN === 'true'

  /**
   * Sign in with email and password
   */
  const handlePasswordLogin = async () => {
    if (!email || !password) return

    setIsSubmitting(true)
    setError(null)
    setErrorField(null)
    setAccountNotFound(false)
    setEmailNotVerified(false)

    try {
      const result = await workosAuthService.loginWithPassword(email, password)

      // VRG-490 §2/D1 — see the identical branch in handleSubmitOtp above.
      if (result.challengeRequired && result.ticket && result.factorType) {
        setChallenge({ ticket: result.ticket, factorType: result.factorType })
        setIsSubmitting(false)
        return
      }

      if (result.enrollmentRequired) {
        setEnrollmentRequired(true)
        setIsSubmitting(false)
        return
      }

      if (result.success && result.user) {
        if (result.isNewUser) {
          navigate(ROUTES.PASSWORD_SETUP, { replace: true })
          return
        }

        setIsLoading(true)
        // VRG-339: see the note in the OTP branch above — one destination, decided at `/`.
        window.location.href = ROUTES.HOME
      } else if (result.errorCode === ErrorCode.AUTH_ACCOUNT_NOT_FOUND) {
        // VERA-1095: distinguished, accepted account-enumeration tradeoff —
        // tell the user their email has no account (and offer to create one)
        // rather than a generic "invalid email or password".
        setAccountNotFound(true)
        setError("We couldn't find an account for that email.")
        setErrorField('email')
        setIsSubmitting(false)
      } else if (result.errorCode === ErrorCode.AUTH_EMAIL_NOT_VERIFIED) {
        // VERA-1095: the password WAS correct — WorkOS just won't authenticate
        // an unverified email. Never show this as "incorrect password"; the
        // dedicated message block below offers Resend + the OTP fallback.
        setEmailNotVerified(true)
        setIsSubmitting(false)
      } else {
        setError(result.error || 'Incorrect password')
        setErrorField('password')
        setIsSubmitting(false)
      }
    } catch (err) {
      console.error('[SignInPage] Error with password login:', err)
      setError('Invalid email or password. Please try again.')
      setIsSubmitting(false)
    }
  }

  /**
   * Resend the signup email-verification link (VERA-1095) — shown when
   * password login reports ErrorCode.AUTH_EMAIL_NOT_VERIFIED.
   */
  const handleResendVerification = async () => {
    if (isResendingVerification || !email) return

    setIsResendingVerification(true)
    try {
      const result = await workosAuthService.requestEmailVerification(email)
      if (result.success) {
        notification({ type: 'success', title: 'Verification email sent' })
      } else {
        notification({ type: 'error', title: result.error || 'Failed to send verification email' })
      }
    } catch (err) {
      console.error('[SignInPage] Error resending verification email:', err)
      notification({ type: 'error', title: 'Failed to send verification email' })
    } finally {
      setIsResendingVerification(false)
    }
  }

  /**
   * Go back to email entry
   */
  const handleChangeEmail = () => {
    setShowOTP(false)
    setShowPasswordLogin(false)
    setPassword('')
    setOtp(['', '', '', '', '', ''])
    setError(null)
    setAccountNotFound(false)
    setEmailNotVerified(false)
    setChallenge(null)
    setEnrollmentRequired(false)
  }

  /** Leave the "Check your inbox" reset confirmation for the sign-in form. */
  const closeResetEmailSent = () => {
    setResetEmailSent(false)
    setError(null)
  }

  /**
   * Resend OTP code
   */
  const handleResendCode = async () => {
    if (isResendingCode) return

    setError(null)
    setOtp(['', '', '', '', '', ''])
    setIsResendingCode(true)

    try {
      const result = await workosAuthService.sendMagicAuthCode(email)

      if (result.success) {
        // INLINE, not the corner snackbar — see `codeResent` where it is declared.
        setCodeResent(true)
      } else {
        setError(result.error || 'Failed to resend verification code')
      }
    } catch (err) {
      console.error('Error resending code:', err)
      setError('Failed to resend verification code. Please try again.')
    } finally {
      setIsResendingCode(false)
    }

  }

  // Check if OTP is complete
  const isOtpComplete = otp.every((digit) => digit !== '')

  // Which field shows the error. `errorField` pins it explicitly (e.g. the
  // "enter your email first" forgot-password case while in password mode);
  // otherwise it follows the active mode.
  const emailFieldError = !!error && (errorField === 'email' || (!showPasswordLogin && !errorField))
  const passwordFieldError = !!error && (errorField === 'password' || (showPasswordLogin && !errorField))

  // VERA-1095: the page's field/button chrome now lives in `authFieldChrome`,
  // NOT here.
  //
  // It was declared inline in this file and, identically, in
  // `AuthPasswordField` — the second hand-roll §36 says to extract on. The two
  // copies had already drifted in the same place: `fieldFill` was made
  // mode-aware and `fieldFillActive` was left as a bare
  // `alpha(text.primary, 0.12)`, so on LIGHT the field darkened to a grey wash
  // under the cursor. `ssoFillHover` and the OTP empty fill had the same
  // defect. See that module for the full reasoning, which moved with them.
  //
  // Nothing is re-derived here any more: the fields take `authFieldRootSx`, the
  // SSO buttons `authSsoButtonSx`, and the OTP boxes `authOtpBoxSx`. The two
  // loose `fieldFill` / `fieldBorder` locals that used to sit on this line are
  // gone — a token held at the call site is how the drift above started.
  //
  // Error tint kept at the literals' own alphas so the field outline stays a
  // touch quieter than the message that explains it.
  const errorText = alpha(theme.palette.error.main, 0.9)

  return (
    <Box
      sx={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        // VERA-1095: the backdrop now belongs to LayoutAuthAmbient, which
        // renders it ONCE for every auth route so navigating does not destroy
        // and re-rasterise the blur layer. This shell is layout only.
        bgcolor: 'transparent',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        overflow: 'hidden',
        // Phone gutter (2026-10-08). The 396px column flex-shrinks to the viewport and
        // ran flush to both screen edges at 390px (measured: column 390 wide); the
        // same 12px PasswordSetup and the callback shell use, which is also the side
        // inset of the phone glass sheet, so the sheet lands flush with the screen.
        px: 3,
        // THE DESKTOP SCALE. Sets `--auth-scale` to `AUTH_DESKTOP_SCALE` on
        // desktop-class devices; every length in the card below is `authPx(n)`,
        // which reads it. Phones and touch tablets keep 1.
        //
        // The VALUE is deliberately not restated here — it was 0.8 from VRG-620
        // and is 1 since VRG-784, and this comment asserted 0.8 for as long as
        // that was false. authScale.ts owns the number and the reasoning.
        ...authScaleSx,
      }}
    >
      {/* Main content. `data-auth-column` is what the ambient lobes centre on
          (SignInBackground `useLogoAnchor`) — the whole form, not just the logo. */}
      <Box
        data-auth-column=""
        sx={{
          // The liquid-glass card — shared with sign-up; see authGlassCard.ts.
          // FIRST, so this column's own `position`/`zIndex` below win.
          ...authGlassCardSx(theme),
          position: 'relative',
          zIndex: 2,
          width: authPx(396),
          // The column's own font size, so the line boxes of the wrappers
          // round each inline link (their strut) scale with the text in them.
          fontSize: authPx(16),
          display: 'flex',
          flexDirection: 'column',
          gap: authPx(60),
          alignItems: 'center',
          // VERA-1095: the two halves of the sign-in <-> sign-up cross-fade.
          //
          // MOUNT (arriving from sign-up): enters moving up from BELOW, the
          // same direction the stepper's incoming panel uses. Sign-in had no
          // entrance at all before, so arriving here was a hard cut even
          // though the page you left faded out.
          //
          // EXIT (`exitSx`, leaving for sign-up): leaves moving UP. Listed
          // second so its `transition`/`opacity` win over the mount animation
          // if a click lands mid-entrance.
          //
          // Only the CONTENT carries this. `SignInBackground` is a fixed
          // sibling above, deliberately outside this subtree, so the blobs and
          // grain stay continuous across the route change — that continuity is
          // what makes the swap read as one movement rather than two pages.
          //
          // COLD LOAD gets its OWN, SHORTER, OPACITY-ONLY entrance — not this
          // one, and not nothing.
          //
          // `c2264a5ce` set cold entry to `animation: 'none'`, and it was right
          // about the defect it measured: on prod with a dark OS you got a WHITE
          // page for the whole bundle download, then the backdrop, then the card
          // fading up — three stages. But the white stage is what made it read as
          // staging, and that is fixed independently, by the pre-paint script the
          // same commit added. What is left without a fade is a card that snaps
          // to full opacity in one frame, which is Spencer's 2026-10-28 report:
          // "on first load the modal should fade in rather than just flash on".
          //
          // The two are different gestures and that is the whole reason this is
          // not simply a revert. The 10px rise is a NAVIGATION cue — it says "you
          // came from the other page", mirroring the outgoing page's rise, and on
          // a cold load there is no other page, so the translate is motion that
          // means nothing and is exactly what reads as an extra stage. An
          // opacity-only fade says "this is arriving" and nothing more.
          //
          // 150ms, not AUTH_TRANSITION_MS's 200: the backdrop has no entrance of
          // its own (it is a fixed sibling, deliberately outside this subtree so
          // the cross-fade stays continuous), so on a cold load it snaps while
          // this fades. Under ~150ms the two read as one arrival; stretch it and
          // they separate again, which is the very thing c2264a5ce removed.
          //
          // Matches sign-up, which has faded in on mount — cold load included,
          // with no equivalent skip — the whole time (`veraSignupContentFadeIn`).
          // The pages being different here is the inconsistency, not the fade.
          '@keyframes veraAuthContentEnter': {
            from: { opacity: 0, transform: 'translateY(10px)' },
            to: { opacity: 1, transform: 'translateY(0)' },
          },
          '@keyframes veraAuthContentColdEnter': {
            from: { opacity: 0 },
            to: { opacity: 1 },
          },
          animation: isColdEntry
            ? `veraAuthContentColdEnter ${AUTH_COLD_ENTER_MS}ms ease-out`
            : `veraAuthContentEnter ${AUTH_TRANSITION_MS}ms ease-out`,
          '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
          ...exitSx,
        }}
      >
        {/* Logo — pulses during loading, links out to vera.inc */}
        <AuthLogo isLoading={isLoading} />

        {/* BACK, TOP-LEFT — the same control sign-up has in the same corner,
            mirroring the theme toggle top-right (Spencer, 2026-09-23: "Back
            button should be a text button like the light/dark mode selector —
            on all sign-in / sign-up"). Portaled + fixed by `AuthBackButton`,
            so this position in the tree is irrelevant to where it draws.

            STEP-AWARE, like sign-up's. On the entry step it goes OUT to
            vera.inc — visitors arrive here from the website's Log in, and
            AuthLogo already names vera.inc as this page's parent. Anywhere
            deeper (code, 2FA, reset-sent) it undoes THAT step instead, the same
            as the in-card control for the state: a "Back" that dropped a
            visitor on the marketing site mid-OTP would be the surprise
            sign-up's Back comment argues against.

            Hidden while `isLoading`: that state is a hand-off already leaving
            this page (a redirect after sign-in, or out to Google/Apple). The
            stall escape inside the card covers the stuck case. */}
        {!isLoading && (
          resetEmailSent ? (
            <AuthBackButton onClick={closeResetEmailSent} />
          ) : challenge || enrollmentRequired || showOTP ? (
            <AuthBackButton onClick={handleChangeEmail} />
          ) : (
            <AuthBackButton href="https://www.vera.inc" />
          )
        )}

        {/* Form Container */}
        <Stack spacing={authPx(8)} width="100%">
          {isLoading ? (
            // The indeterminate bar that used to sit here was DELETED, not
            // restyled. It was 396px wide by 3px tall — a 132:1 rule — filled with
            // a `transparent -> brand -> transparent` sliver, so it had no leading
            // edge to track and read as a stray divider with a smudge on it rather
            // than as an indicator. Three further problems it could not be styled
            // out of: it sat at the top of the form's old box leaving a large void
            // beneath it; it duplicated `AuthLogo`'s pulse, which already says
            // "working" more elegantly; and an indeterminate PROGRESS affordance
            // overstates what is happening — this state is a hand-off lasting a
            // redirect, not a task with progress to report.
            //
            // What replaces it is the pulsing wordmark (already there, via
            // `isLoading` on AuthLogo) plus one line naming the destination. The
            // pair is centred in the space the form vacated instead of hugging its
            // top edge, so the screen reads as deliberate rather than emptied.
            // `py` was 10 (40px) on top of the wordmark's own margin, which left the one
            // line of copy stranded ~100px below the mark — two isolated elements rather
            // than the single centred pair the note above intends. And at 14px
            // `text.secondary` it was the quietest type on the page while being the ONLY
            // type on the page. 15px at full contrast, closer in.
            <Stack alignItems="center" justifyContent="center" py={authPx(24)} spacing={authPx(8)}>
              <Typography sx={{ fontSize: authPx(15), lineHeight: authPx(24), color: 'text.primary', textAlign: 'center' }}>
                {loadingMessage}
              </Typography>
              {/*
                The stall admission. Rendered ALWAYS and revealed with opacity/visibility
                rather than mounted on demand, so its arrival cannot shift the line above
                it — the same reason skeletons hold their footprint (§19). `aria-live`
                announces it when it appears, since a user who cannot see the screen is
                exactly the one most stuck here.
              */}
              <Stack
                aria-live="polite"
                alignItems="center"
                spacing={authPx(4)}
                sx={{
                  opacity: slowRedirect ? 1 : 0,
                  visibility: slowRedirect ? 'visible' : 'hidden',
                  transition: 'opacity 200ms ease',
                }}
              >
                <Typography sx={{ fontSize: authPx(13), lineHeight: authPx(24), color: 'text.secondary', textAlign: 'center' }}>
                  This is taking longer than usual.
                </Typography>
                <Link
                  component="button"
                  type="button"
                  underline="always"
                  onClick={() => {
                    setIsLoading(false)
                    setSlowRedirect(false)
                  }}
                  sx={{ fontSize: authPx(13), color: 'text.primary' }}
                >
                  Back to sign in
                </Link>
              </Stack>
            </Stack>
          ) : resetEmailSent ? (
            // Password reset email sent confirmation
            <Stack spacing={authPx(8)}>
              <Typography sx={AUTH_TITLE_SX}>
                Check your inbox
              </Typography>
              <Typography sx={{ fontSize: authPx(14), color: 'text.secondary', lineHeight: 1.5 }}>
                We sent a password reset link to{' '}
                <Box component="strong" sx={{ color: 'text.primary' }}>
                  {email}
                </Box>
                . Follow the link in the email to set a new password.
              </Typography>
              {/* Was a bare `Typography` with an onClick — 13px `text.disabled`,
                  not focusable, not announced as a control. Now the shared auth
                  Back treatment (2026-09-23), which is a real button. */}
              <AuthBackButton placement="inline" label="Back to sign in" onClick={closeResetEmailSent} />
            </Stack>
          ) : challenge ? (
            // VRG-490 §2 — a verified second factor withheld the session;
            // this completes it via POST /auth/2fa/challenge/verify.
            <TwoFactorChallenge
              ticket={challenge.ticket}
              factorType={challenge.factorType}
              onBack={handleChangeEmail}
              onVerified={(result) => {
                // A low remaining count is worth a heads-up NOW — the next
                // screen the user sees is Home, with no obvious prompt to
                // check their recovery codes.
                if (
                  result.usedRecoveryCode &&
                  typeof result.remainingRecoveryCodes === 'number' &&
                  result.remainingRecoveryCodes <= 2
                ) {
                  notification({
                    type: 'warning',
                    title: `Only ${result.remainingRecoveryCodes} recovery code${result.remainingRecoveryCodes === 1 ? '' : 's'} left`,
                  })
                }
                setIsLoading(true)
                // VRG-339: same destination as every other login path — `/`
                // decides where this user lands.
                window.location.href = ROUTES.HOME
              }}
            />
          ) : enrollmentRequired ? (
            // VRG-490 D1 — a platform_admin with no verified factor at all.
            <TwoFactorEnrollmentRequired onBack={handleChangeEmail} />
          ) : (
            // VRG-460: both step panels live in ONE grid cell and cross-fade.
            // The wrapper's height follows the ACTIVE panel so it grows and
            // shrinks as part of the same movement rather than snapping ahead
            // of it — same duration and easing as the fade, for that reason.
            <Box
              sx={{
                display: 'grid',
                width: '100%',
                // NOT decorative — both of the next two are load-bearing, and
                // both were found missing here by measuring against the
                // stepper rather than by reading the copy back.
                //
                // Grid items default to `align-items: stretch`, so without this
                // each panel stretches to the wrapper's OWN explicit height and
                // `getBoundingClientRect()` hands that height straight back
                // instead of the panel's real content height. The measurement
                // becomes self-referential and a genuinely shorter step can
                // never shrink the box once it has been tall. Measured before
                // this line: both panels reported 505px, i.e. the wrapper's
                // height, not their own.
                alignItems: 'start',
                // The horizontal twin of the same trap. With no explicit
                // template the single implicit column is `auto` — sized to the
                // MAX-CONTENT of every panel sharing the cell, not to this
                // track's width — so the wider panel silently sets the column
                // for both and can overflow the 396px auth column. The `0`
                // minimum is the part doing the work: a bare `1fr` still floors
                // at min-content.
                gridTemplateColumns: 'minmax(0, 1fr)',
                // Clip the outgoing panel to the animating height. `clip` is
                // preferred over `hidden` because `hidden` creates a SCROLL
                // container, which lets the browser scroll it during the
                // transition and leaves the column offset afterwards; `clip`
                // clips identically with nothing to scroll. `hidden` stays as
                // the fallback because an unsupported `clip` is simply dropped,
                // leaving `overflow: visible` and both panels painted on top of
                // each other — far worse than the offset it fixes.
                overflow: 'hidden',
                '@supports (overflow: clip)': { overflow: 'clip' },
                // `undefined` (auto) until the observer has reported at least
                // once, so the un-measured case is never a stale number that
                // could clip a panel. See the observer's own comment.
                height: activePanelHeight ? `${activePanelHeight}px` : undefined,
                transition: prefersReducedMotion ? 'none' : `height ${STEP_TRANSITION_MS}ms ease`,
              }}
            >
            {/* Step 1 — verification code. Second in step order, first in the
                DOM (see `stepIndex`'s note on why that is fine). */}
            <Box
              ref={(node: HTMLDivElement | null) => {
                panelRefs.current[1] = node
              }}
              aria-hidden={!showOTP}
              sx={stepPanelSx(1)}
            >
            {/* THE TITLE IS FOR SCREEN READERS ONLY (Spencer, 2026-09-23:
                "should we remove 'Welcome back' — cleaner?"). The logo and
                the card say where you are and the button names the action, so
                a visible title repeated both. It stays as the page's h1 so a
                screen-reader user can still land on it. OUTSIDE the Stack on
                purpose: a hidden first child would still push its sibling
                down by the Stack's 24px gap. */}
            <Typography component="h1" sx={VISUALLY_HIDDEN}>
              Sign in to Vera
            </Typography>
            <Stack spacing={authPx(12)}>

              {/* Divider and Change Email Button */}
              <Stack spacing={authPx(6)}>
                <Box sx={{ height: '1px', bgcolor: 'divider', width: '100%' }} />
                {/* A navigation back (to the email step), so it wears the auth
                    Back treatment shared with the corner controls (2026-09-23)
                    rather than its own full-ink, no-hover button. */}
                <AuthBackButton placement="inline" label="Change email" onClick={handleChangeEmail} />
              </Stack>

              {/* Instructions */}
              <Typography
                sx={{
                  fontFamily: 'Lato, sans-serif',
                  fontSize: authPx(14),
                  lineHeight: 1.4,
                  color: 'text.primary',
                }}
              >
                {`Please enter the 6-digit verification code that was sent to ${email}`}
              </Typography>

              {/* OTP Input Fields */}
              <Stack spacing={authPx(8)}>
                {/* The row FILLS the column, so the digits and the Submit button
                    beneath them share one width and read as a single block. It
                    used to be six 48px squares at a 4px gap — 308px of content
                    in a 396px column, stopping 88px short of the button's right
                    edge. Each cell is now `flex: 1 1 0` and the boxes size
                    themselves from whatever width the column has (see
                    `authOtpBoxSx` for the square-ness and the chrome).

                    `useFlexGap` so the gap is a real `gap` rather than Stack's
                    default margin-left: with `flex-basis: 0` cells the two are
                    equivalent in width terms, but `gap` is the honest expression
                    of "fixed space between, everything else shared" and it does
                    not put a margin on a child that also owns its own box. */}
                {/* VRG-610 — the shared `CodeInput`. This row and the 2FA challenge's
                    were near-identical hand-rolls; the behaviour now lives in one
                    place, and `onComplete` submits the moment the sixth digit lands
                    (typed or pasted) instead of waiting for the button. */}
                <CodeInput
                  autoFocus
                  value={otp.join('')}
                  onChange={(next) => {
                    setOtp(Array.from({ length: 6 }, (_, i) => next[i] ?? ''))
                    if (error) setError(null)
                  }}
                  onComplete={(next) => handleSubmitOtp(next)}
                  disabled={isSubmitting}
                  aria-label="Sign-in code"
                />

                <Stack spacing={authPx(2)}>
                  <Typography
                    sx={{
                      fontSize: authPx(14),
                      lineHeight: 1.4,
                      color: 'text.secondary',
                    }}
                  >
                    This code is valid for 10 minutes.
                  </Typography>
                  {error && (
                    <Typography sx={{ fontSize: authPx(14), lineHeight: authPx(24), color: errorText }}>
                      {error}
                    </Typography>
                  )}
                </Stack>
              </Stack>

              {/* The bespoke `&:disabled` override that used to sit here is GONE — its
                  three declarations are now what `AuthPrimaryButton` does by default, so
                  it was re-stating two of them and getting the third wrong.

                  The wrong one: `color: alpha(common.white, 0.52)`, justified by a comment
                  about dimming "against the PURPLE it sits on". There is no purple. That
                  gradient became `alpha(text.primary, 0.95)` — near-WHITE in dark mode —
                  so this painted white text at 52% on a near-white button and the label
                  disappeared. The comment outlived the colour it described.

                  `AuthPrimaryButton` now dims `background.default`, which inverts with the
                  mode the way the fill does. See its `&:disabled`. */}
              <AuthPrimaryButton
                fullWidth
                // Wrapped — `handleSubmitOtp` now takes an optional code, so passing
                // it by reference would submit the MouseEvent as the code.
                onClick={() => handleSubmitOtp()}
                disabled={!isOtpComplete}
                loading={isSubmitting}
              >
                Submit
              </AuthPrimaryButton>

              {/* Resend Code Link */}
              <Box sx={{ textAlign: 'center', py: authPx(4) }}>
                <Typography
                  sx={{
                    fontSize: authPx(14),
                    lineHeight: 1.4,
                    color: 'text.primary',
                  }}
                >
                  Didn't receive the code?{' '}
                  <Link
                    component="button"
                    onClick={handleResendCode}
                    disabled={isResendingCode}
                    sx={{
                      color: isResendingCode ? 'text.disabled' : 'text.primary',
                      textDecoration: 'underline',
                      cursor: isResendingCode ? 'default' : 'pointer',
                      // VERA-1095: `component="button"` renders a real <button>
                      // element inside this <Typography> — a button element
                      // carries its own UA-stylesheet line-height/padding and
                      // (in MUI's Link) `display: inline-flex` by default, none
                      // of which match the plain text run beside it, so the
                      // word "Try again" sat off the "Didn't receive the code?"
                      // baseline. Reset it to read as an inline text run: no
                      // padding/border, inherit the font + line-height from the
                      // Typography instead of the button's own, and pin the
                      // baseline explicitly.
                      display: 'inline',
                      padding: 0,
                      border: 0,
                      background: 'none',
                      font: 'inherit',
                      lineHeight: 'inherit',
                      verticalAlign: 'baseline',
                      '&:hover': {
                        textDecoration: 'underline',
                      },
                      '&.Mui-disabled': {
                        color: 'text.disabled',
                      },
                    }}
                  >
                    {isResendingCode ? 'Sending…' : 'Try again'}
                  </Link>
                </Typography>
              </Box>

              {/*
                THE "code sent" CONFIRMATION, in the column directly under the control that
                caused it, replacing a bottom-right snackbar.

                ALWAYS RENDERED, revealed with opacity — so its arrival and departure cannot
                move the Submit button or the resend line. A confirmation that reflows the
                form it confirms is worse than no confirmation, and this one appears and
                disappears on a timer with no user action, which is the worst case for a
                layout shift (§19's footprint-parity argument, applied to a transient).

                `aria-live="polite"` because the visual cue is the whole message: a screen
                reader user pressed "Try again" and otherwise gets nothing back. The old
                snackbar announced itself; an inline div does not unless told to.

                Deliberately quiet — a line of text with a check, not the bordered success
                slab the snackbar drew. In a column this narrow a full alert box reads as an
                error state at a glance, and nothing has gone wrong.
              */}
              {/*
                `color` on the row, inherited by the icon (lucide draws with
                `currentColor`), rather than reading the palette onto the icon directly.
                Fewer moving parts, same result.

                COSTS ONE TS2859. This file emits "excessive complexity comparing types
                'Theme' and 'Theme'" — TypeScript giving up on two structurally identical
                Theme types, because pnpm resolves @mui/material twice (react-18 and
                react-19 peer trees). It already emitted two; adding any `sx` prop here
                makes a third, taking the platform count 1035 -> 1036.

                I first assumed the palette read was to blame and removed it. Measured: no
                change, still 1036. It is the `sx` itself, not what is in it. Keeping the
                simplification anyway, but the comment is corrected rather than left
                asserting a cause I disproved.

                Not worked around, because every workaround is worse: `style` cannot carry
                the reduced-motion query or a theme token. The real fix is the duplicate
                @types/react / @mui resolution (pnpm `packageExtensions`), which is a
                lockfile change and does not belong in a UI commit.
              */}
              <Box
                aria-live="polite"
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: authPx(4),
                  minHeight: authPx(20),
                  color: 'success.main',
                  opacity: codeResent ? 1 : 0,
                  visibility: codeResent ? 'visible' : 'hidden',
                  transition: 'opacity 200ms ease',
                  '@media (prefers-reduced-motion: reduce)': { transition: 'none' },
                }}
              >
                <CheckIcon size={14} strokeWidth={1.5} />
                <Typography sx={{ fontSize: authPx(13), lineHeight: 1.4, color: 'text.secondary' }}>
                  {otpCodeWasSent}
                </Typography>
              </Box>
            </Stack>
            </Box>

            {/* Step 0 — email entry (with password by default, VRG-460). */}
            <Box
              ref={(node: HTMLDivElement | null) => {
                panelRefs.current[0] = node
              }}
              aria-hidden={showOTP}
              sx={stepPanelSx(0)}
            >
            {/* THE TITLE IS FOR SCREEN READERS ONLY (Spencer, 2026-09-23:
                "should we remove 'Welcome back' — cleaner?"). The logo and
                the card say where you are and the button names the action, so
                a visible title repeated both. It stays as the page's h1 so a
                screen-reader user can still land on it. OUTSIDE the Stack on
                purpose: a hidden first child would still push its sibling
                down by the Stack's 24px gap. */}
            <Typography component="h1" sx={VISUALLY_HIDDEN}>
              Sign in to Vera
            </Typography>
            <Stack spacing={authPx(12)}>

              {/* Page-level error — belongs to no field (social sign-in, callback
                  failures). Rendered here rather than as field helperText so it
                  cannot masquerade as a wrong password. */}
              {error && errorField === 'form' && (
                <Alert severity="error" description={error} />
              )}

              {/* Form */}
              <Stack spacing={authPx(24)} pt={authPx(16)}>
                {/* Email Input Field */}
                <Stack spacing={authPx(12)}>
                <Stack spacing={authPx(8)}>
                  <Typography
                    sx={AUTH_FIELD_LABEL_SX}
                  >
                    Email
                  </Typography>
                  <TextField
                    fullWidth
                    placeholder="Enter your email"
                    value={email}
                    onChange={(e) => { setEmail(e.target.value); setError(null); setErrorField(null); setAccountNotFound(false); setEmailNotVerified(false) }}
                    onKeyPress={(e) => {
                      if (e.key === 'Enter' && email) {
                        showPasswordLogin ? handlePasswordLogin() : handleReceiveCode()
                      }
                    }}
                    error={emailFieldError}
                    sx={{
                      '& .MuiInputBase-root': authFieldRootSx(theme),
                      // Kill the autofill fill so the field keeps its own translucent bg.
                      // TWO different paints have to be defeated, because two different
                      // things fill an autofilled input:
                      //   - dark mode: MUI itself hardcodes an inset box-shadow
                      //     (`0 0 0 100px #266798 inset`, OutlinedInput.js) whenever
                      //     palette.mode !== 'light'. Only box-shadow:none removes it.
                      //   - light mode: MUI nulls that, so Chrome's own UA background-color
                      //     (#e8f0fe) shows instead. background-clip:text clips the painted
                      //     background to the glyphs, which removes it from the field.
                      // background-clip does NOT touch box-shadow and box-shadow:none does
                      // NOT touch the UA background, so neither line alone is sufficient.
                      // Clipping the input's own background costs nothing here: the visible
                      // bg lives on .MuiInputBase-root (the wrapper), not on the input.
                      ...fieldAutofillResetSx(theme),
                      '& .MuiInputBase-input::placeholder': {
                        color: 'text.secondary',
                        opacity: 1,
                      },
                      '& .MuiFormHelperText-root': {
                        color: errorText,
                        fontSize: authPx(14),
                        margin: `${authPx(8)} 0 0 0`,
                      },
                    }}
                    helperText={emailFieldError ? error : undefined}
                  />
                </Stack>

                {showPasswordLogin ? (
                  <>
                    {/* Password field */}
                    <Stack spacing={authPx(8)}>
                      <Stack direction="row" justifyContent="space-between" alignItems="center">
                        <Typography
                          sx={AUTH_FIELD_LABEL_SX}
                        >
                          Password
                        </Typography>
                        {/* Forgot password — emails a code, then routes to set a new password */}
                        <Link
                          component="button"
                          type="button"
                          onClick={handleForgotPassword}
                          disabled={isSendingCode}
                          sx={{
                            // Dark takes the field label's colour (AUTH_FIELD_LABEL_SX):
                            // the lobes now sit behind the form, and zinc-400 at 13px
                            // measured 4.49:1 on a passing lobe — a hair under AA.
                            color: (theme) =>
                              theme.palette.mode === 'dark'
                                ? `color-mix(in srgb, ${theme.palette.text.primary} 80%, transparent)`
                                : `color-mix(in srgb, ${theme.palette.text.primary} 84%, transparent)`,
                            textDecoration: 'none',
                            cursor: 'pointer',
                            fontSize: authPx(14),
                            lineHeight: 1.4,
                            '&:hover': { textDecoration: 'underline', color: 'text.primary' },
                          }}
                        >
                          Forgot password?
                        </Link>
                      </Stack>
                      <AuthPasswordField
                        name="password"
                        placeholder="Enter your password"
                        value={password}
                        onChange={(value) => { setPassword(value); setError(null); setErrorField(null) }}
                        onKeyPress={(e) => {
                          if (e.key === 'Enter' && email && password) {
                            handlePasswordLogin()
                          }
                        }}
                        error={passwordFieldError}
                        helperText={passwordFieldError ? error ?? undefined : undefined}
                      />
                    </Stack>

                    {/* Sign In button */}
                    <AuthPrimaryButton
                      fullWidth
                      onClick={handlePasswordLogin}
                      disabled={!email || !password}
                      loading={isSubmitting}
                    >
                      Sign in
                    </AuthPrimaryButton>

                    {/* VERA-1095: shown only when the password path reported no
                        account for this email — offers the resolving action
                        instead of leaving the user stuck. */}
                    {accountNotFound && (
                      <Box sx={{ textAlign: 'center', py: authPx(4) }}>
                        <Link
                          component="button"
                          onClick={() => leaveTo(ROUTES.SIGN_UP_SELF_SERVE)}
                          sx={{
                            color: 'text.primary',
                            textDecoration: 'underline',
                            cursor: 'pointer',
                            fontSize: authPx(14),
                            lineHeight: 1.4,
                            '&:hover': { textDecoration: 'underline' },
                          }}
                        >
                          Create an account
                        </Link>
                      </Box>
                    )}

                    {/* VERA-1095: password was correct, the account's email
                        just isn't verified yet. Offers Resend + the OTP
                        fallback instead of reading as a wrong password. */}
                    {emailNotVerified && (
                      <Box sx={{ textAlign: 'center', py: authPx(4) }}>
                        <Typography sx={{ fontSize: authPx(13), lineHeight: 1.5, color: 'text.secondary' }}>
                          Verify your email to sign in with a password.{' '}
                          <Link
                            component="button"
                            onClick={handleResendVerification}
                            disabled={isResendingVerification}
                            sx={{
                              color: isResendingVerification ? 'text.disabled' : 'text.primary',
                              textDecoration: 'underline',
                              cursor: isResendingVerification ? 'default' : 'pointer',
                              '&:hover': { textDecoration: 'underline' },
                              '&.Mui-disabled': { color: 'text.disabled' },
                            }}
                          >
                            {isResendingVerification ? 'Sending…' : 'Resend verification email'}
                          </Link>
                          . You can also sign in with an emailed code instead.
                        </Typography>
                      </Box>
                    )}

                    {/* Toggle back to OTP */}
                    <Box sx={{ textAlign: 'center', py: authPx(4) }}>
                      <Link
                        component="button"
                        onClick={() => {
                          setShowPasswordLogin(false)
                          setPassword('')
                          setError(null)
                          setAccountNotFound(false)
                          setEmailNotVerified(false)
                        }}
                        sx={{
                          color: 'text.secondary',
                          textDecoration: 'underline',
                          cursor: 'pointer',
                          fontSize: authPx(15),
                          lineHeight: 1.4,
                          '&:hover': { textDecoration: 'underline' },
                        }}
                      >
                        Use verification code instead
                      </Link>
                    </Box>
                  </>
                ) : (
                  <>
                    {/* Receive Code Button */}
                    <AuthPrimaryButton
                      fullWidth
                      onClick={handleReceiveCode}
                      disabled={!email}
                      loading={isSendingCode}
                    >
                      Receive code
                    </AuthPrimaryButton>

                    {/* Toggle to password login */}
                    <Box sx={{ textAlign: 'center', py: authPx(4) }}>
                      <Link
                        component="button"
                        onClick={() => {
                          setShowPasswordLogin(true)
                          setError(null)
                        }}
                        sx={{
                          color: 'text.secondary',
                          textDecoration: 'underline',
                          cursor: 'pointer',
                          fontSize: authPx(14),
                          lineHeight: 1.4,
                          '&:hover': { textDecoration: 'underline' },
                        }}
                      >
                        Sign in with password
                      </Link>
                    </Box>
                  </>
                )}
                </Stack>


                {/* Divider with "or" */}
                <Stack direction="row" spacing={authPx(8)} alignItems="center">
                  <Box sx={{ flex: 1, height: '1px', bgcolor: 'divider' }} />
                  <Typography
                    sx={{
                      fontFamily: 'Inter, sans-serif',
                      fontSize: authPx(13),
                      // body1's line-height is a fixed 24px in the theme, so it
                      // is restated here to scale with the type.
                      lineHeight: authPx(24),
                      // Was `#6c7278`, a one-off grey that appears nowhere else
                      // in the app. It is a label on a divider — the quietest
                      // kind of text there is — so it takes the same token as
                      // every other piece of supporting copy on the page.
                      color: 'text.secondary',
                      letterSpacing: authPx(-0.12),
                    }}
                  >
                    or
                  </Typography>
                  <Box sx={{ flex: 1, height: '1px', bgcolor: 'divider' }} />
                </Stack>

                {/* Social sign-in buttons — gap matches the email-input → Receive-code
                    gap (the form's spacing={authPx(12)}). */}
                <Stack spacing={authPx(12)}>
                {/* Google Sign In Button */}
                <MuiButton
                  fullWidth
                  onClick={handleGoogleSignIn}
                  startIcon={<GoogleIcon />}
                  sx={authSsoButtonSx(theme)}
                >
                  Sign in with Google
                </MuiButton>

                {appleLoginEnabled && (
                  <MuiButton
                    fullWidth
                    onClick={handleAppleSignIn}
                    startIcon={<AppleIcon />}
                    sx={authSsoButtonSx(theme)}
                  >
                    Sign in with Apple
                  </MuiButton>
                )}
                </Stack>

                {/* Persistent link to self-serve sign-up (VERA-1095) — there was
                    previously no path from sign-in to sign-up at all. */}
                <Box sx={{ textAlign: 'center' }}>
                  <Typography sx={{ fontSize: authPx(15), lineHeight: 1.4, color: 'text.secondary' }}>
                    Don't have an account?{' '}
                    <AuthInlineLink onClick={() => leaveTo(ROUTES.SIGN_UP_SELF_SERVE)}>
                      Create one
                    </AuthInlineLink>
                  </Typography>
                </Box>
              </Stack>
            </Stack>
            </Box>
            </Box>
          )}
        </Stack>
      </Box>
    </Box>
  )
}
